import { Box, Card, CardContent, Typography, Stack, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, Chip, TextField, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import dayjs from "../../lib/dayjs";
import { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ScheduleModal } from "../../components/Appointments/ScheduleModal";
import { DoctorDayCalendar, DoctorSlot } from "../../components/Appointments/DoctorDayCalendar";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getMySchedulesByDate,
  getDoctorSchedulesByDate,
  type DoctorScheduleWithAppointments,
  removeDoctorScheduleSlot,
  getMyLinkedDoctors,
  type DoctorSearchResult,
} from "../../services/doctors.service";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { createAppointmentFromSchedule, rescheduleAppointment, confirmAppointmentPresence } from "../../services/appointments.service";
import { findUserByCpf } from "../../services/usersList.service";
import { toast } from "sonner";
import {
  listGoogleEventsForDay,
  type GoogleCalendarEvent,
  getGoogleIntegrationStatus,
} from "../../services/google.service";
import { findRecordIdForAppointment } from "../../services/medicalRecords.service";

export function Schedule() {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.userInfo);
  const location = useLocation() as any;
  const initialDoctorIdFromState: string | undefined = location?.state?.doctorId;

  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [openScheduleModal, setOpenScheduleModal] = useState(false);
  const [modalDate, setModalDate] = useState<Dayjs | null>(null);
  const [modalTime, setModalTime] = useState<string | null>(null);
  const [openDatePicker, setOpenDatePicker] = useState(false);

  const [schedules, setSchedules] = useState<DoctorScheduleWithAppointments[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0); // usado para forçar recarregar a agenda

  // Eventos do Google Calendar para o dia selecionado (sincronizados manualmente pelo médico)
  const [googleEvents, setGoogleEvents] = useState<GoogleCalendarEvent[]>([]);
  const [loadingGoogleEvents, setLoadingGoogleEvents] = useState(false);
  const [googleEventsError, setGoogleEventsError] = useState<string | null>(null);

  // SECRETARY - médicos vinculados
  const [linkedDoctors, setLinkedDoctors] = useState<DoctorSearchResult[]>([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | "">(initialDoctorIdFromState || "");
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [doctorsError, setDoctorsError] = useState<string | null>(null);
  const [selectedAppointmentSlot, setSelectedAppointmentSlot] = useState<DoctorSlot | null>(null);
  const [selectedLocalId, setSelectedLocalId] = useState<string | null>(null);

  const [openNewAppointmentModal, setOpenNewAppointmentModal] = useState(false);
  const [newAppointmentDateTime, setNewAppointmentDateTime] = useState<Dayjs | null>(null);
  const [newAppointmentSlot, setNewAppointmentSlot] = useState<DoctorSlot | null>(null);
  const [cpfSearch, setCpfSearch] = useState<string>("");
  const [foundPatient, setFoundPatient] = useState<any | null>(null);
  const [searchingPatient, setSearchingPatient] = useState(false);
  const [patientSearchError, setPatientSearchError] = useState<string>("");
  const [creatingAppointment, setCreatingAppointment] = useState(false);
  const [removingScheduleSlot, setRemovingScheduleSlot] = useState(false);

  const [openRescheduleModal, setOpenRescheduleModal] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState<Dayjs | null>(null);
  const [rescheduleTime, setRescheduleTime] = useState<string>("");
  const [rescheduling, setRescheduling] = useState(false);

  const [openPresenceModal, setOpenPresenceModal] = useState(false);
  const [presenceStatus, setPresenceStatus] = useState<"PRESENTE" | "AUSENTE">("PRESENTE");
  const [presencePin, setPresencePin] = useState<string>("");

  const roleUpper = String(user?.role || "").toUpperCase();
  const isDoctor = roleUpper === "DOCTOR" || roleUpper === "DOCTOR_OWNER";
  const isSecretary = roleUpper === "SECRETARY";
  const canManageSchedules = isDoctor || isSecretary;

  // Garante um local selecionado quando o usuário for MÉDICO
  useEffect(() => {
    if (!isDoctor) return;
    const doctorLocals = user?.doctor?.locals ?? [];
    if (!doctorLocals.length) return;

    const exists = selectedLocalId && doctorLocals.some((l: any) => l.id === selectedLocalId);
    if (!exists) {
      setSelectedLocalId(doctorLocals[0].id);
    }
  }, [isDoctor, user?.doctor?.locals, selectedLocalId]);

  // Garante um local selecionado para SECRETÁRIA de acordo com o médico escolhido
  useEffect(() => {
    if (!isSecretary) return;
    const currentDoctor = linkedDoctors.find((d) => d.doctorId === selectedDoctorId);
    const doctorLocals = currentDoctor?.locals ?? [];
    if (!doctorLocals.length) return;

    const exists = selectedLocalId && doctorLocals.some((l) => l.id === selectedLocalId);
    if (!exists) {
      setSelectedLocalId(doctorLocals[0].id);
    }
  }, [isSecretary, linkedDoctors, selectedDoctorId, selectedLocalId, initialDoctorIdFromState]);

  // Carrega médicos vinculados quando usuário for SECRETARY
  useEffect(() => {
    if (!isSecretary) return;

    let ignore = false;
    async function loadDoctors() {
      try {
        setLoadingDoctors(true);
        setDoctorsError(null);
        const list = await getMyLinkedDoctors();
        if (ignore) return;
        setLinkedDoctors(list);

        if (list.length === 0) {
          setSelectedDoctorId("");
          return;
        }

        // 1) Se veio doctorId do Dashboard e ele existe na lista, usa ele
        if (
          initialDoctorIdFromState &&
          list.some((d) => d.doctorId === initialDoctorIdFromState)
        ) {
          setSelectedDoctorId(initialDoctorIdFromState);
          return;
        }

        // 2) Se já existe um selectedDoctorId válido, mantém
        if (
          selectedDoctorId &&
          list.some((d) => d.doctorId === selectedDoctorId)
        ) {
          return; // não altera
        }

        // 3) Caso contrário, usa o primeiro médico da lista
        setSelectedDoctorId(list[0].doctorId);
      } catch (e) {
        if (!ignore) {
          console.error(e);
          setDoctorsError("Não foi possível carregar os médicos vinculados. Tente novamente.");
        }
      } finally {
        if (!ignore) setLoadingDoctors(false);
      }
    }

    loadDoctors();
    return () => {
      ignore = true;
    };
  }, [isSecretary, initialDoctorIdFromState, selectedDoctorId]);

  // Carrega a agenda do médico (médico logado ou médico selecionado pela secretária)
  useEffect(() => {
    let ignore = false;
    async function load() {
      // Para secretária, só carrega se já houver médico selecionado
      if (isSecretary && !selectedDoctorId) {
        if (!ignore) {
          setSchedules([]);
          setGoogleEvents([]);
        }
        return;
      }

      setLoading(true);
      try {
        const dateStr = selectedDate.format("YYYY-MM-DD");
        let schedulesResponse: DoctorScheduleWithAppointments[] = [];

        if (isDoctor) {
          schedulesResponse = await getMySchedulesByDate(dateStr);
        } else if (isSecretary && selectedDoctorId) {
          schedulesResponse = await getDoctorSchedulesByDate(selectedDoctorId, dateStr);
        } else {
          schedulesResponse = [];
        }

        console.log(
          "[AGENDA] Dia:",
          selectedDate.toISOString(),
          "| schedules recebidos:",
          schedulesResponse.length
        );
        schedulesResponse.slice(0, 5).forEach((s, i) => {
          console.log(
            `[AGENDA] SCHEDULE[${i}] startDate=${s.startDate} endDate=${s.endDate} appointments=${s.appointments?.length ?? 0}`
          );
        });

        if (!ignore) {
          setSchedules(schedulesResponse);
          setGoogleEvents([]);
        }
      } catch (e) {
        if (!ignore) {
          console.error(e);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }
    if (isDoctor || isSecretary) {
      load();
    }
    return () => { ignore = true };
  }, [selectedDate, reloadVersion, isDoctor, isSecretary, selectedDoctorId]);

  // Converte schedules + appointments do backend em slots para o calendário,
  // incluindo bloqueios vindos de eventos do Google Calendar.
  const slots: DoctorSlot[] = useMemo(() => {
    if (!schedules) return [];

    const result: DoctorSlot[] = [];

    const filteredSchedules = selectedLocalId
      ? schedules.filter((s) => s.local?.id === selectedLocalId)
      : schedules;

    // 1) Gera slots baseados nos schedules (disponibilidade local + appointments)
    for (const s of filteredSchedules) {
      const start = dayjs(s.startDate);
      const end = dayjs(s.endDate);

      // Garante que os blocos sejam sempre gerados no DIA selecionado na agenda,
      // usando apenas a hora/minuto vindos do backend. Isso evita problemas de timezone
      // onde o startDate/endDate vêm em outro dia (ex.: 29 vs 30 em UTC).
      const scheduleStart = selectedDate
        .hour(start.hour())
        .minute(start.minute())
        .second(0)
        .millisecond(0);

      const scheduleEnd = selectedDate
        .hour(end.hour())
        .minute(end.minute())
        .second(0)
        .millisecond(0);

      // Gera blocos de 30 minutos entre startDate e endDate
      let pointer = scheduleStart;
      while (pointer.isBefore(scheduleEnd)) {
        const blockEnd = pointer.add(30, "minute");

        // Verifica se existe appointment que cubra esse bloco.
        // Normalizamos também os horários dos appointments para o DIA selecionado,
        // comparando apenas hora/minuto (ignorando o dia original vindo do backend).
        const coveringAppointment = s.appointments.find((appt) => {
          const origStart = dayjs(appt.startTime);
          const origEnd = dayjs(appt.endTime);

          const apptStart = selectedDate
            .hour(origStart.hour())
            .minute(origStart.minute())
            .second(0)
            .millisecond(0);

          const apptEnd = selectedDate
            .hour(origEnd.hour())
            .minute(origEnd.minute())
            .second(0)
            .millisecond(0);

          // bloco está totalmente dentro do appointment
          return pointer.isSameOrAfter(apptStart) && blockEnd.isSameOrBefore(apptEnd);
        });

        // Verifica se existe evento do Google que cubra esse bloco (quando carregados)
        // Ignora eventos "Horário disponível - Sagrada Visão", que representam apenas
        // uma disponibilidade criada pela própria aplicação.
        const coveringGoogleEvent = googleEvents.find((evt) => {
          if (!evt.start || !evt.end) return false;

          const summary = (evt.summary || "").trim().toLowerCase();
          if (summary === "horário disponível - sagrada visão") {
            return false;
          }

          const evtStart = dayjs(evt.start);
          const evtEnd = dayjs(evt.end);
          return pointer.isBefore(evtEnd) && blockEnd.isAfter(evtStart);
        });

        let status: DoctorSlot["status"] = "AVAILABLE";
        let title: string | null = null;

        if (coveringAppointment) {
          // Em caso de conflito entre appointment e Google, o appointment sempre vence
          status = "BOOKED";
          title = coveringAppointment.patient?.name ?? null;
        } else if (coveringGoogleEvent) {
          status = "UNAVAILABLE";
          title = coveringGoogleEvent.summary || null;
        }

        result.push({
          start: pointer.toISOString(),
          end: blockEnd.toISOString(),
          status,
          scheduleId: s.scheduleId,
          appointmentId: coveringAppointment?.id,
          appointmentStatus: coveringAppointment?.status,
          patientId: coveringAppointment?.patient?.id ?? null,
          patientName: coveringAppointment?.patient?.name ?? null,
          source: coveringGoogleEvent ? "GOOGLE" : "LOCAL",
          title,
        });

        pointer = blockEnd;
      }
    }

    // 2) Gera blocos "virtuais" apenas com eventos do Google, mesmo fora dos schedules
    //    mantendo a regra: se já existir slot (especialmente BOOKED), não sobrescreve.
    if (googleEvents.length > 0) {
      // Gera blocos ao longo do dia inteiro (00:00 até 23:59),
      // mesmo que a interface só exiba parte desse intervalo.
      const dayStart = selectedDate
        .startOf("day");

      const dayEnd = selectedDate
        .endOf("day");

      let pointer = dayStart;
      while (pointer.isBefore(dayEnd)) {
        const blockEnd = pointer.add(30, "minute");

        const coveringGoogleEvent = googleEvents.find((evt) => {
          if (!evt.start || !evt.end) return false;

          const summary = (evt.summary || "").trim().toLowerCase();
          if (summary === "horário disponível - sagrada visão") {
            return false;
          }

          const evtStart = dayjs(evt.start);
          const evtEnd = dayjs(evt.end);
          return pointer.isBefore(evtEnd) && blockEnd.isAfter(evtStart);
        });

        if (coveringGoogleEvent) {
          const existingSlot = result.find((slot) =>
            dayjs(slot.start).isSame(pointer, "minute")
          );

          // Se já existir slot e ele estiver BOOKED (consulta), mantemos o appointment.
          // Caso não exista slot, criamos um UNAVAILABLE de origem GOOGLE.
          if (!existingSlot) {
            result.push({
              start: pointer.toISOString(),
              end: blockEnd.toISOString(),
              status: "UNAVAILABLE",
              scheduleId: undefined,
              appointmentId: undefined,
              appointmentStatus: undefined,
              patientName: null,
              source: "GOOGLE",
              title: coveringGoogleEvent.summary || null,
            });
          }
        }

        pointer = blockEnd;
      }
    }

    console.log(
      "[AGENDA] slots gerados:",
      result.length,
      "| primeiros:",
      result.slice(0, 10).map((s) => `${dayjs(s.start).format("HH:mm")}(${s.status}${s.source ? "/" + s.source : ""})`)
    );

    return result;
  }, [schedules, selectedDate, selectedLocalId, googleEvents]);

  const handleSelectTime = (dateTime: Dayjs, slot: DoctorSlot | null) => {
    // Se não há schedule para esse horário (slot nulo ou UNAVAILABLE)
    // abrimos o modal para criar uma nova disponibilidade para MÉDICOS ou SECRETÁRIAS vinculadas.
    if (!slot || slot.status === "UNAVAILABLE") {
      if (!canManageSchedules) return;
      setModalDate(dateTime);
      setModalTime(dateTime.format("HH:mm"));
      setOpenScheduleModal(true);
      return;
    }

    // Se há schedule e o horário está livre (AVAILABLE), abrimos o modal de novo agendamento (consulta).
    if (slot.status === "AVAILABLE") {
      setNewAppointmentDateTime(dateTime);
      setNewAppointmentSlot(slot);
      setOpenNewAppointmentModal(true);
    }
  };

  const handleCloseScheduleModal = () => {
    setOpenScheduleModal(false);
  };

  const handleConfirmPresence = () => {
    if (!selectedAppointmentSlot?.appointmentId) return;

    // abre modal para escolher se o paciente esteve presente ou ausente
    setPresenceStatus("PRESENTE");
    setPresencePin("");
    setOpenPresenceModal(true);
  };

  const handleSyncGoogleCalendar = async () => {
    if (!isDoctor) return;

    try {
      setGoogleEventsError(null);
      setLoadingGoogleEvents(true);

      const status = await getGoogleIntegrationStatus();

      if (!status.connected) {
        if (status.authUrl) {
          // Médico ainda não conectou: abre o fluxo de conexão com o Google
          window.location.href = status.authUrl;
        } else {
          toast.error("Não foi possível iniciar a integração com o Google Calendar.");
        }
        return;
      }

      // Médico já está conectado: carrega os eventos do dia selecionado
      const events = await listGoogleEventsForDay(selectedDate.toISOString());
      setGoogleEvents(events);
    } catch (err) {
      console.error(err);
      toast.error("Não foi possível sincronizar com o Google Calendar. Tente novamente.");
    } finally {
      setLoadingGoogleEvents(false);
    }
  };

  const handleReschedule = () => {
    if (!selectedAppointmentSlot?.appointmentId) return;

    const start = dayjs(selectedAppointmentSlot.start);
    setRescheduleDate(start);
    setRescheduleTime(start.format("HH:mm"));
    setOpenRescheduleModal(true);
  };

  const handleConfirmReschedule = async () => {
    if (!selectedAppointmentSlot?.appointmentId || !rescheduleDate || !rescheduleTime) return;

    const [hour, minute] = rescheduleTime.split(":").map(Number);
    const newDateTime = rescheduleDate.hour(hour).minute(minute).second(0).millisecond(0);

    try {
      setRescheduling(true);
      await rescheduleAppointment({
        appointmentId: selectedAppointmentSlot.appointmentId,
        newStartTime: newDateTime.toISOString(),
      });

      toast.success("Consulta reagendada com sucesso.");
      setOpenRescheduleModal(false);
      setSelectedAppointmentSlot(null);
      setReloadVersion((v) => v + 1);
    } catch (e) {
      console.error(e);
      toast.error("Não foi possível reagendar a consulta. Tente novamente.");
    } finally {
      setRescheduling(false);
    }
  };

  const handleSearchPatient = async () => {
    setPatientSearchError("");
    setFoundPatient(null);

    const raw = cpfSearch.trim();
    if (!raw) return;

    setSearchingPatient(true);
    try {
      const user = await findUserByCpf(raw);
      if (!user) {
        setPatientSearchError("Nenhum paciente encontrado para este CPF.");
        return;
      }
      setFoundPatient(user);
    } catch {
      // erro já tratado no service com toast
    } finally {
      setSearchingPatient(false);
    }
  };

  const handleCreateAppointment = async () => {
    if (!foundPatient || !newAppointmentDateTime || !newAppointmentSlot?.scheduleId) return;

    setCreatingAppointment(true);
    try {
      await createAppointmentFromSchedule({
        scheduleId: newAppointmentSlot.scheduleId,
        patientId: foundPatient.id,
        startTime: newAppointmentDateTime.toISOString(),
      });
      setOpenNewAppointmentModal(false);
      setReloadVersion((v) => v + 1);
    } catch (e) {
      console.error(e);
    } finally {
      setCreatingAppointment(false);
    }
  };

  const handleRemoveAvailableSlot = async () => {
    if (!newAppointmentSlot?.scheduleId || !newAppointmentSlot.start || !newAppointmentSlot.end) return;

    setRemovingScheduleSlot(true);
    try {
      await removeDoctorScheduleSlot({
        scheduleId: newAppointmentSlot.scheduleId,
        slotStart: newAppointmentSlot.start,
        slotEnd: newAppointmentSlot.end,
      });
      toast.success("Horário removido da agenda.");
      setOpenNewAppointmentModal(false);
      setNewAppointmentSlot(null);
      setNewAppointmentDateTime(null);
      setReloadVersion((v) => v + 1);
    } catch (e) {
      console.error(e);
      toast.error("Não foi possível remover o horário. Tente novamente.");
    } finally {
      setRemovingScheduleSlot(false);
    }
  };

  const handleConfirmPresenceSave = async () => {
    if (!selectedAppointmentSlot?.appointmentId) return;

    if (presenceStatus === "PRESENTE" && !presencePin.trim()) {
      toast.error("PIN obrigatório quando status é PRESENTE.");
      return;
    }

    try {
      await confirmAppointmentPresence({
        appointmentId: selectedAppointmentSlot.appointmentId,
        pin: presencePin.trim(),
        presenceStatus,
      });

      toast.success("Presença atualizada com sucesso.");
      setOpenPresenceModal(false);
      setSelectedAppointmentSlot(null);
      setPresencePin("");
      setReloadVersion((v) => v + 1);
    } catch (e: any) {
      console.error(e);
      const message = e?.response?.data?.message;
      if (typeof message === "string") {
        toast.error(message);
      } else if (Array.isArray(message) && message.length > 0) {
        toast.error(String(message[0]));
      } else {
        toast.error("Não foi possível atualizar a presença. Verifique o PIN e tente novamente.");
      }
    }
  };

  return (
    <Box
      width="100%"
      padding={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", mt: 0 }}>
        {isSecretary && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ mb: 2 }}
          >
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel id="secretary-doctor-select-label">Médico vinculado</InputLabel>
              <Select
                labelId="secretary-doctor-select-label"
                label="Médico vinculado"
                value={selectedDoctorId || ""}
                onChange={(e) => setSelectedDoctorId(e.target.value as string)}
              >
                {linkedDoctors.map((doc) => (
                  <MenuItem key={doc.doctorId} value={doc.doctorId}>
                    {doc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {loadingDoctors && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Carregando médicos vinculados...
                </Typography>
              </Stack>
            )}

            {!loadingDoctors && linkedDoctors.length === 0 && !doctorsError && (
              <Typography variant="body2" color="text.secondary">
                Nenhum médico vinculado a esta secretária.
              </Typography>
            )}

            {doctorsError && (
              <Typography variant="body2" color="error">
                {doctorsError}
              </Typography>
            )}
          </Stack>
        )}

        <Card sx={{ boxShadow: "0 6px 20px rgba(0,0,0,0.08)", borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6" fontWeight={600} color="#133859">
                {isDoctor ? "Minha agenda" : "Agenda"}
              </Typography>

              {isDoctor && (
                <Button
                  variant="outlined"
                  onClick={handleSyncGoogleCalendar}
                  disabled={loadingGoogleEvents}
                >
                  Sincronizar agenda
                </Button>
              )}
            </Stack>

            {loading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }} spacing={2}>
                <CircularProgress size={28} />
                <Typography variant="body2" color="text.secondary">
                  Carregando agenda do dia...
                </Typography>
              </Stack>
            ) : (
              <DoctorDayCalendar
                date={selectedDate}
                slots={slots}
                onSelectTime={handleSelectTime}
                onOpenCalendar={() => setOpenDatePicker(true)}
                onChangeDate={(day) => setSelectedDate(day)}
                onAppointmentClick={(slot) => setSelectedAppointmentSlot(slot)}
                locals={
                  isDoctor
                    ? user?.doctor?.locals
                    : isSecretary
                    ? (linkedDoctors.find((d) => d.doctorId === selectedDoctorId)?.locals ?? [])
                    : undefined
                }
                selectedLocalId={selectedLocalId}
                onChangeLocal={(id) => setSelectedLocalId(id)}
              />
            )}

            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "rgba(25, 118, 210, 0.7)" }} />
              <Typography variant="caption" color="text.secondary">
                Blocos azuis representam horários já agendados.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "rgba(46, 125, 50, 0.4)" }} />
              <Typography variant="caption" color="text.secondary">
                Blocos verdes representam horários disponíveis para novos agendamentos.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Modal para criar disponibilidade (schedule) quando clica em horário sem agenda */}
      <ScheduleModal
        open={openScheduleModal}
        onClose={handleCloseScheduleModal}
        initialDate={modalDate}
        initialTime={modalTime}
        onScheduleCreated={() => setReloadVersion((v) => v + 1)}
        doctorId={
          isDoctor
            ? user?.doctor?.doctorId ?? ""
            : isSecretary
            ? selectedDoctorId || ""
            : ""
        }
        localId={
          selectedLocalId
            ? selectedLocalId
            : isDoctor
            ? user?.doctor?.locals?.[0]?.id ?? null
            : isSecretary
            ? linkedDoctors.find((d) => d.doctorId === selectedDoctorId)?.locals?.[0]?.id ?? null
            : null
        }
        locals={
          isDoctor
            ? (user?.doctor?.locals as any)
            : isSecretary
            ? (linkedDoctors.find((d) => d.doctorId === selectedDoctorId)?.locals as any) ?? []
            : []
        }
      />

      {/* Modal de criação de novo agendamento a partir de um horário livre dentro de um schedule */}
      <Dialog open={openNewAppointmentModal} onClose={() => setOpenNewAppointmentModal(false)} fullWidth maxWidth="sm">
        <DialogTitle>Novo agendamento</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Horário selecionado:
            </Typography>
            <Typography variant="h6" color="#133859">
              {newAppointmentDateTime ? newAppointmentDateTime.format("DD/MM/YYYY HH:mm") : "--"}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Informe o CPF do paciente para localizar o cadastro.
            </Typography>

            <TextField
              label="CPF do paciente"
              type="text"
              fullWidth
              value={cpfSearch}
              onChange={(e) => setCpfSearch(e.target.value)}
            />

            <Button variant="outlined" onClick={handleSearchPatient} disabled={searchingPatient || !cpfSearch.trim()}>
              {searchingPatient ? "Buscando..." : "Buscar paciente"}
            </Button>

            {patientSearchError && (
              <Typography variant="body2" color="error">
                {patientSearchError}
              </Typography>
            )}

            {foundPatient && (
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                  backgroundColor: "#fafafa",
                }}
              >
                <Typography variant="subtitle2" color="#133859">
                  Paciente selecionado
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Nome: {foundPatient.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  E-mail: {foundPatient.email}
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewAppointmentModal(false)} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleRemoveAvailableSlot}
            color="error"
            disabled={!canManageSchedules || !newAppointmentSlot?.scheduleId || removingScheduleSlot}
          >
            {removingScheduleSlot ? "Removendo..." : "Remover horário"}
          </Button>
          <Button
            onClick={handleCreateAppointment}
            variant="contained"
            disabled={!foundPatient || !newAppointmentDateTime || !newAppointmentSlot?.scheduleId || creatingAppointment}
          >
            {creatingAppointment ? "Criando..." : "Criar agendamento"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de detalhes do agendamento (quando clica em um bloco BOOKED) */}
      <Dialog
        open={!!selectedAppointmentSlot}
        onClose={() => setSelectedAppointmentSlot(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Consulta agendada</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {selectedAppointmentSlot && (
            <Card
              variant="outlined"
              sx={{
                mt: 1,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Stack spacing={1.5}>
                  <Typography variant="h6" fontWeight={600} color="#133859">
                    Paciente
                  </Typography>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Nome:
                    </Typography>
                    <Chip
                      size="small"
                      label={selectedAppointmentSlot.patientName || "Não informado"}
                    />
                  </Stack>

                  <Divider sx={{ my: 1 }} />

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "repeat(2, minmax(0, 1fr))",
                        sm: "repeat(4, minmax(0, 1fr))",
                      },
                      gap: 2,
                      mt: 0.5,
                    }}
                  >
                    <Stack>
                      <Typography variant="caption" color="text.secondary">
                        Data
                      </Typography>
                      <Typography fontWeight={600} color="#133859">
                        {dayjs(selectedAppointmentSlot.start).format("DD/MM/YYYY")}
                      </Typography>
                    </Stack>
                    <Stack>
                      <Typography variant="caption" color="text.secondary">
                        Início
                      </Typography>
                      <Typography fontWeight={600} color="#133859">
                        {dayjs(selectedAppointmentSlot.start).format("HH:mm")}
                      </Typography>
                    </Stack>
                    <Stack>
                      <Typography variant="caption" color="text.secondary">
                        Fim
                      </Typography>
                      <Typography fontWeight={600} color="#133859">
                        {dayjs(selectedAppointmentSlot.end).format("HH:mm")}
                      </Typography>
                    </Stack>
                    <Stack>
                      <Typography variant="caption" color="text.secondary">
                        Status
                      </Typography>
                      <Chip
                        size="small"
                        color={selectedAppointmentSlot.appointmentStatus === "CONFIRMADO" ? "success" : "warning"}
                        label={selectedAppointmentSlot.appointmentStatus || "Desconhecido"}
                      />
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 3, pb: 2 }}>
          <Button
            onClick={handleConfirmPresence}
            variant="outlined"
            color="primary"
            disabled={!selectedAppointmentSlot}
          >
            Confirmar presença
          </Button>
          <Button
            onClick={handleReschedule}
            variant="outlined"
            color="primary"
            disabled={!selectedAppointmentSlot}
          >
            Reagendar
          </Button>
          {canManageSchedules && selectedAppointmentSlot?.appointmentId && (
            <>
              {selectedAppointmentSlot.patientId && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    const patientId = selectedAppointmentSlot.patientId!;
                    setSelectedAppointmentSlot(null);
                    navigate(`/paciente/${patientId}/historico`);
                  }}
                >
                  Histórico Clínico
                </Button>
              )}
              <Button
                variant="outlined"
                color="primary"
                onClick={async () => {
                  const slot = selectedAppointmentSlot;
                  setSelectedAppointmentSlot(null);
                  const doctorId = isDoctor
                    ? user?.doctor?.doctorId ?? ""
                    : selectedDoctorId || "";
                  const patientId = slot.patientId ?? "";
                  const existingId = patientId
                    ? await findRecordIdForAppointment(patientId, slot.appointmentId!)
                    : null;
                  if (existingId) {
                    navigate(`/prontuario/${existingId}`);
                  } else {
                    navigate(
                      `/prontuario/novo?appointmentId=${slot.appointmentId}&doctorId=${doctorId}${patientId ? `&patientId=${patientId}` : ""}`,
                    );
                  }
                }}
              >
                Prontuário
              </Button>
            </>
          )}
          <Button onClick={() => setSelectedAppointmentSlot(null)} color="inherit">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de reagendamento */}
      <Dialog open={openRescheduleModal} onClose={() => setOpenRescheduleModal(false)} fullWidth maxWidth="xs">
        <DialogTitle>Reagendar consulta</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Escolha a nova data e horário da consulta.
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <DateCalendar
                  disablePast
                  value={rescheduleDate}
                  onChange={(newValue) => setRescheduleDate(newValue)}
                />
              </Box>

              <TextField
                label="Horário"
                type="time"
                value={rescheduleTime}
                onChange={(e) => setRescheduleTime(e.target.value)}
                fullWidth
                inputProps={{ step: 300 }}
              />
            </Stack>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRescheduleModal(false)} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmReschedule}
            variant="contained"
            disabled={!rescheduleDate || !rescheduleTime || rescheduling}
          >
            {rescheduling ? "Reagendando..." : "Confirmar novo horário"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmação de presença */}
      <Dialog open={openPresenceModal} onClose={() => setOpenPresenceModal(false)} fullWidth maxWidth="xs">
        <DialogTitle>Confirmar presença</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Selecione se o paciente esteve presente ou ausente na consulta.
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel id="presence-status-label">Status</InputLabel>
              <Select
                labelId="presence-status-label"
                value={presenceStatus}
                label="Status"
                onChange={(e) => setPresenceStatus(e.target.value as "PRESENTE" | "AUSENTE")}
              >
                <MenuItem value="PRESENTE">Presente</MenuItem>
                <MenuItem value="AUSENTE">Ausente</MenuItem>
              </Select>
            </FormControl>

            {presenceStatus === "PRESENTE" && (
              <TextField
                label="PIN de confirmação"
                type="password"
                value={presencePin}
                onChange={(e) => setPresencePin(e.target.value)}
                fullWidth
                inputProps={{ maxLength: 8 }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPresenceModal(false)} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleConfirmPresenceSave} variant="contained">
            Salvar status
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDatePicker} onClose={() => setOpenDatePicker(false)} fullWidth maxWidth="xs">
        <DialogTitle>Selecionar dia</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              value={selectedDate}
              onChange={(value) => {
                if (value) {
                  setSelectedDate(value);
                }
              }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDatePicker(false)} color="inherit">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
